import React, { useCallback, useContext } from 'react';
import {
  HeaderPhraseCell,
  AddPhrase,
  AddOutput,
  HeaderExecutionOrderCell,
  GlobalHeaderActionsCell,
} from 'domains/designers/TruthTable/header-cells';
import {
  removeColumnWithEvents,
  deleteOutputDataItemWithEvents,
  addColumnBeforeWithEvents,
  addColumnAfterWithEvents,
  moveColumnToLeftWithEvents,
  moveColumnToRightWithEvents,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/TruthTable/TruthTableEditService';
import { HeaderOutputCells } from 'domains/designers/TruthTable/header-cells/HeaderOutputCells';
import {
  PhraseItemClickCallback,
  HeaderActionCallback,
} from 'domains/designers/TruthTable/header-cells/HeaderPhraseCell';
import {
  RuleDesignerSurface,
  RuleOutputDisplayData,
  RuleServiceStepNotification,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service';
import { HandleNotificationsCallback, getHeaderActions, getRows, getHeaders } from '../TruthTableUtils';
import './TruthTableHeader.less';
import { EvaluationColumn } from '../columns';
import { RuleDesignerContext } from 'domains/designers/RuleDesigner/RuleDesignerContext';
import { DataPair } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ContextData';
import { addHeaderActionWithEvents } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/TruthTable/TruthTableEditService';
import { IRootGroupModel } from 'domains/designers/RuleDesigner/models/RootGroupModel';

export interface ITruthTableHeaderProps extends React.HTMLProps<HTMLTableSectionElement> {
  rootModel: IRootGroupModel;
  showExecutionNumber?: boolean;
  onAnchorClick?: PhraseItemClickCallback;
  onVerbClick?: PhraseItemClickCallback;
  onAddConditionClick: () => void;
  onAddOutputClick: () => void;
  onEditOutputClick: (result: RuleOutputDisplayData) => void;
  handleNotifications: HandleNotificationsCallback;
}

/**
 * Header row for regular and external truth tables.
 * Contains cells to configure phrases (Anchor and Verb) and outputs.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function TruthTableHeader({
  rootModel,
  showExecutionNumber,
  onAnchorClick,
  onVerbClick,
  onAddConditionClick,
  onAddOutputClick,
  handleNotifications,
  onEditOutputClick,
  ...otherProps
}: ITruthTableHeaderProps) {
  const { isDebug, designerSurface } = useContext(RuleDesignerContext);
  const headers = getHeaders(rootModel);
  const columnCount = headers.length;

  const headerActions = getHeaderActions(getRows(rootModel)[0]);

  const handleEventsResponse = useCallback(
    (response: DataPair[]) => {
      const notifications = response.map(item => item.OutputValue as RuleServiceStepNotification);
      handleNotifications(notifications);
    },
    [handleNotifications]
  );

  const onDeletePhraseClick = useCallback(
    async (index: number) => {
      const headers = rootModel.RuleDisplayData.TableHeaders;
      if (index < headers.length) {
        await removeColumnWithEvents({
          ruleSessionId: rootModel.RuleSessionId,
          headerId: headers[index].Id,
        }).then(handleEventsResponse);
      }
    },
    [handleEventsResponse, rootModel]
  );

  const onDeleteOutputClick = useCallback(
    async ({ DataDescriptionName }: RuleOutputDisplayData) => {
      await deleteOutputDataItemWithEvents({
        itemName: DataDescriptionName,
        ruleSessionId: rootModel.RuleSessionId,
      }).then(handleEventsResponse);
    },
    [handleEventsResponse, rootModel.RuleSessionId]
  );

  const onAddBeforeClick: HeaderActionCallback = useCallback(
    async ({ header }) => {
      await addColumnBeforeWithEvents({
        ruleSessionId: rootModel.RuleSessionId,
        beforeHeaderId: header.Id,
      }).then(handleEventsResponse);
    },
    [handleEventsResponse, rootModel.RuleSessionId]
  );

  const onAddAfterClick: HeaderActionCallback = useCallback(
    async ({ header }) => {
      await addColumnAfterWithEvents({
        ruleSessionId: rootModel.RuleSessionId,
        afterHeaderId: header.Id,
      }).then(handleEventsResponse);
    },
    [handleEventsResponse, rootModel.RuleSessionId]
  );

  const onMoveColumnToLeft: HeaderActionCallback = useCallback(
    async ({ header }) => {
      await moveColumnToLeftWithEvents({
        ruleSessionId: rootModel.RuleSessionId,
        headerId: header.Id,
      }).then(handleEventsResponse);
    },
    [handleEventsResponse, rootModel.RuleSessionId]
  );

  const onMoveColumnToRight: HeaderActionCallback = useCallback(
    async ({ header }) => {
      await moveColumnToRightWithEvents({
        ruleSessionId: rootModel.RuleSessionId,
        headerId: header.Id,
      }).then(handleEventsResponse);
    },
    [handleEventsResponse, rootModel.RuleSessionId]
  );

  const handleAddHeaderAction = useCallback(async (actionType: string) => {
    addHeaderActionWithEvents({
      ruleSessionId: rootModel.RuleSessionId,
      actionTypeName: actionType
    }).then(handleEventsResponse);
  }, [handleEventsResponse, rootModel.RuleSessionId]);

  return (
    <thead {...otherProps}>
      <tr className={'tt-table__header-row'}>
        {designerSurface !== RuleDesignerSurface.ExternalTruthTable && <HeaderExecutionOrderCell />}
        {headers.map((header, index) => (
          <HeaderPhraseCell
            key={header.Id}
            sessionId={rootModel.RuleSessionId}
            onAnchorClick={onAnchorClick}
            onVerbClick={onVerbClick}
            isDebugMode={isDebug}
            header={header}
            canDelete={columnCount > 1}
            onDeleteClick={() => onDeletePhraseClick(index)}
            onAddBefore={onAddBeforeClick}
            onAddAfter={onAddAfterClick}
            onMoveColumnToLeft={onMoveColumnToLeft}
            onMoveColumnToRight={onMoveColumnToRight}
          />
        ))}
        {!isDebug && <AddPhrase onAdd={onAddConditionClick} />}
        <HeaderOutputCells
          rootModel={rootModel}
          onDeleteClick={onDeleteOutputClick}
          onEditOutputClick={onEditOutputClick}
        />
        {headerActions?.map(action => {
          return (
            <GlobalHeaderActionsCell
              key={action.ActionId}
              action={action}
              handler={action.getHandler()}
              isDebugMode={isDebug}
            />
          );
        })}
        {!isDebug && <AddOutput rootModel={rootModel} onAddOutput={onAddOutputClick} onAddHeaderAction={handleAddHeaderAction} />}
        <EvaluationColumn isHeader />
      </tr>
    </thead>
  );
}
